import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Loader, Copy, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '', honeypot: '' });
  const [state, setState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (state !== 'success') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const particles = Array.from({ length: 24 }, (_, i) => ({
      x: canvas.width / 2, y: canvas.height / 2,
      angle: (i / 24) * Math.PI * 2,
      speed: 1.5 + Math.random() * 2,
      r: Math.random() * 3 + 1.5,
      alpha: 1,
      color: Math.random() > 0.5 ? '#8DC21F' : '#5A8C1A',
    }));
    let frame = 0, animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += Math.cos(p.angle) * p.speed;
        p.y += Math.sin(p.angle) * p.speed;
        p.alpha -= 0.012;
        const hex = Math.floor(p.alpha * 255).toString(16).padStart(2, '0');
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + hex;
        ctx.fill();
      });
      frame++;
      if (frame < 140) animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, [state]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state === 'loading') return;
    setState('loading');
    setErrorMsg('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact/send`, {
        name: form.name, email: form.email,
        company: form.company || undefined,
        subject: form.subject, message: form.message, honeypot: form.honeypot,
      });
      if (res.data.status === 'success') setState('success');
      else { setErrorMsg('Unexpected response.'); setState('error'); }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error sending. Please try again.';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setState('error');
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText('innovation@valorizasm.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const reset = () => {
    setForm({ name: '', email: '', company: '', subject: '', message: '', honeypot: '' });
    setState('idle');
    setErrorMsg('');
  };

  const inp = `w-full rounded-xl px-4 py-3 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[rgba(141,194,31,0.45)]`;
  const inpStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      style={{ background: '#000', paddingTop: '7rem', paddingBottom: '7rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}
    >
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center"
          >
            <span className="v-tag green mb-6 inline-flex">{t('contact.tag')}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              {t('contact.h1a')}{' '}
              <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>
                {t('contact.h1b')}
              </em>
            </h2>
            <p className="text-base text-white/55 leading-relaxed mb-8 max-w-[44ch]">{t('contact.sub')}</p>
            <button
              onClick={copyEmail}
              className="flex items-center gap-4 p-4 rounded-xl text-left"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(141,194,31,0.12)', border: '1px solid rgba(141,194,31,0.2)' }}>
                <Mail size={18} style={{ color: '#8DC21F' }} />
              </div>
              <div>
                <div className="text-sm font-semibold text-white/85">innovation@valorizasm.com</div>
                <div className="text-xs text-white/40">{copied ? t('contact.copied') : t('contact.copyEmail')}</div>
              </div>
            </button>
            <div className="mt-8 gradient-line w-32" />
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div
              className="rounded-[28px] p-6 md:p-8 relative overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.09)', backdropFilter: 'blur(8px)' }}
            >
              <AnimatePresence mode="wait">
                {state === 'success' ? (
                  <motion.div
                    key="success"
                    data-testid="contact-form-success-state"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex flex-col items-center justify-center py-12 text-center min-h-[400px]"
                  >
                    <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
                    <div className="relative mb-6">
                      <div
                        className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
                        style={{ background: 'rgba(141,194,31,0.15)', border: '1px solid rgba(141,194,31,0.35)', boxShadow: '0 0 30px rgba(141,194,31,0.2)' }}
                      >
                        <CheckCircle size={28} style={{ color: '#8DC21F' }} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{t('contact.successTitle')}</h3>
                    <p className="text-sm text-white/55 mb-1 max-w-[30ch]">{t('contact.successBody')}</p>
                    <p className="text-xs text-[#8DC21F] mb-8">{t('contact.successNote')}</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={copyEmail} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>
                        <Copy size={14} />{copied ? t('contact.copied') : t('contact.copyEmail')}
                      </button>
                      <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(141,194,31,0.1)', border: '1px solid rgba(141,194,31,0.2)', color: '#8DC21F', cursor: 'pointer' }}>
                        {t('contact.sendAnother')}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <input name="honeypot" value={form.honeypot} onChange={handleChange} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-white/45 font-semibold mb-2">{t('contact.nameLabel')} *</label>
                        <input data-testid="contact-form-name-input" name="name" type="text" required placeholder={t('contact.namePlaceholder')} value={form.name} onChange={handleChange} className={inp} style={inpStyle} />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-white/45 font-semibold mb-2">{t('contact.emailLabel')} *</label>
                        <input data-testid="contact-form-email-input" name="email" type="email" required placeholder={t('contact.emailPlaceholder')} value={form.email} onChange={handleChange} className={inp} style={inpStyle} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-white/45 font-semibold mb-2">{t('contact.companyLabel')}</label>
                      <input data-testid="contact-form-company-input" name="company" type="text" placeholder={t('contact.companyPlaceholder')} value={form.company} onChange={handleChange} className={inp} style={inpStyle} />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-white/45 font-semibold mb-2">{t('contact.subjectLabel')} *</label>
                      <input name="subject" type="text" required placeholder={t('contact.subjectPlaceholder')} value={form.subject} onChange={handleChange} className={inp} style={inpStyle} />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-white/45 font-semibold mb-2">{t('contact.msgLabel')} *</label>
                      <textarea data-testid="contact-form-message-textarea" name="message" required placeholder={t('contact.msgPlaceholder')} value={form.message} onChange={handleChange} rows={5} className={`${inp} resize-none leading-relaxed`} style={inpStyle} />
                    </div>

                    <AnimatePresence>
                      {state === 'error' && (
                        <motion.div data-testid="contact-form-error-text" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                          <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                          <p className="text-xs text-white/70">{errorMsg}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <button
                      data-testid="contact-form-submit-button"
                      type="submit"
                      disabled={state === 'loading'}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold"
                      style={{ background: state === 'loading' ? 'rgba(141,194,31,0.4)' : '#8DC21F', color: '#000', border: 'none', cursor: state === 'loading' ? 'not-allowed' : 'pointer', boxShadow: '0 8px 24px rgba(141,194,31,0.2)', fontFamily: 'Manrope, sans-serif' }}
                    >
                      {state === 'loading' ? <><Loader size={16} className="animate-spin" />{t('contact.sending')}</> : <><Send size={16} />{t('contact.submit')}</>}
                    </button>
                    <p className="text-center text-xs text-white/30">{t('contact.legal')}</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
